import { Text } from '@mantine/core';
import { useQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedRowModel,
	createFacetedUniqueValues,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createDataTableColumnHelper } from '@apple/ui/data-table/utils/column-helper';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import type { Category, CategoryListingFilters } from '@apple/features/product/models/category';
import { categoriesOdataQueryOptions } from '@apple/features/product/queries/odata';

const features = createDataTableFeatures({
	sortedRowModel: createSortedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/categories')({
	component: () => CategoryManagementRoute(),
	beforeLoad: args => {
		requireAuth(args, {
			requirePermissions: [
				'Manzanita.Security.Features.ProductCategoryManagement.Read',
				'Manzanita.Security.Features.ProductCategoryManagement.Write',
			],
		});
	},
});

function CategoryManagementRoute() {
	const { t } = useTranslation('manage');
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const search = Route.useSearch();

	const defaultFilters: CategoryListingFilters = {};

	const tableState = useTableState<Category, CategoryListingFilters>({
		search,
		navigate,
		defaultFilters: defaultFilters,
		defaultSorting: [{ id: 'description', desc: false }],
		fieldMap: [],
	});

	const [categorySearchQuery] = useQueries({
		queries: [
			categoriesOdataQueryOptions({
				pagination: tableState.state.pagination,
				filters: tableState.currentFilterData,
				sorting: tableState.state.sorting,
			}),
		],
	});

	const columnHelper = createDataTableColumnHelper<Category, CategoryListingFilters>();

	const table = useTable({
		features,
		data: categorySearchQuery.data.rows,
		rowCount: categorySearchQuery.data.totalRowCount,
		...tableState,
		manualSorting: true,
		manualPagination: true,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		columnResizeMode: 'onChange',
		columns: columnHelper.columns([
			columnHelper.accessor('categoryCode', {
				enableColumnFilter: false,
				header: t('categories.fields.categoryCode'),
				cell: ({ row }) => (
					<Link
						to='/manage/categories/$categoryId'
						state={{ from: location }}
						params={{ categoryId: row.original.categoryId }}
						title={t('categories.edit.title')}
					>
						<Text size='sm'>{row.original.categoryCode}</Text>
					</Link>
				),
			}),
			columnHelper.accessor('description', {
				header: t('categories.fields.description'),
				filter: {
					group: t('categories.filterGroups.details'),
				},
			}),
		]),
	});

	return (
		<TableLayout
			title={t('categories.title')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('categories.add.title')}
					icon={AddIcon}
					onClick={() => {
						void navigate({ to: '/manage/categories/add', state: { from: location } });
					}}
				/>,
			]}
		>
			<DataTable table={table} loading={categorySearchQuery.isFetching} />
		</TableLayout>
	);
}
